import type { SimShareRequest, SimShareResponse } from '@cruz/shared';
import { useEffect, useState } from 'react';
import { ReportButton } from './Report.tsx';
import { Sheet } from './Sheet.tsx';
import { navigate } from '../challenge/router.ts';
import { simApi, simErrorMessage } from './api.ts';
import { Notice, PageTitle, primaryButton, secondaryButton, Working } from './ui.tsx';

/** Share cards: the sheet that makes one and shares it, and its public page. */

const shareText = (s: SimShareResponse) =>
  `${s.card.phrase} — ${s.card.nickname}, ${s.card.brotherhood}. ¿Te atreves con tu carrera cofrade?`;

const smallButton =
  'inline-flex min-h-10 items-center gap-1.5 rounded-full border border-gold/70 bg-paper px-4 py-1.5 ' +
  'text-sm font-semibold text-purple transition-colors hover:bg-cream-200 dark:border-gold/50 ' +
  'dark:bg-night-700 dark:text-gold-300 dark:hover:bg-night-800';

export function ShareButton({
  request,
  label = 'Compartir',
}: {
  request: SimShareRequest;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className={smallButton} onClick={() => setOpen(true)}>
        <svg
          width={16}
          height={16}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M4 12v7a1 1 0 001 1h14a1 1 0 001-1v-7M16 6l-4-4-4 4M12 2v14" />
        </svg>
        {label}
      </button>
      {open && <ShareSheet request={request} onClose={() => setOpen(false)} />}
    </>
  );
}

function ShareSheet({ request, onClose }: { request: SimShareRequest; onClose: () => void }) {
  const [share, setShare] = useState<SimShareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const { kind, ref } = request;

  useEffect(() => {
    let alive = true;
    simApi
      .share({ kind, ref })
      .then((s) => alive && setShare(s))
      .catch((err: unknown) => alive && setError(simErrorMessage(err)));
    return () => {
      alive = false;
    };
  }, [kind, ref]);

  const canShare = typeof navigator.share === 'function';
  const nativeShare = async () => {
    if (!share) return;
    const data: ShareData = { title: share.card.title, text: shareText(share), url: share.url };
    try {
      // With the image when the phone can (Android, iOS 15+)
      const blob = await (await fetch(share.image_story)).blob();
      const file = new File([blob], `logro-${share.card.id}.png`, { type: 'image/png' });
      if (navigator.canShare?.({ files: [file] })) data.files = [file];
    } catch {
      // Without the image: the link still has its preview
    }
    try {
      await navigator.share(data);
    } catch {
      // Cancelled
    }
  };
  const copy = async () => {
    if (!share) return;
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Sheet title="Compártelo" onClose={onClose}>
      {!share && !error && <Working label="Preparando tu tarjeta…" />}
      {error && <Notice tone="error">{error}</Notice>}
      {share && (
        <div className="space-y-3">
          <img
            src={share.image_story}
            alt={`Tarjeta: ${share.card.kicker}. ${share.card.title}. ${share.card.phrase}`}
            width={1080}
            height={1920}
            className="mx-auto h-auto max-h-[46dvh] w-auto rounded-xl border border-gold/60 shadow-lg"
          />
          {canShare && (
            <button type="button" className={primaryButton} onClick={() => void nativeShare()}>
              Compartir
            </button>
          )}
          <a
            className={canShare ? secondaryButton : primaryButton}
            href={`https://wa.me/?text=${encodeURIComponent(`${shareText(share)} ${share.url}`)}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Enviar por WhatsApp
          </a>
          <a
            className={secondaryButton}
            href={share.image_story}
            download={`logro-${share.card.id}.png`}
          >
            Descargar imagen
          </a>
          <button type="button" className={secondaryButton} onClick={() => void copy()}>
            {copied ? '¡Enlace copiado!' : 'Copiar enlace'}
          </button>
        </div>
      )}
    </Sheet>
  );
}

/** /simulador/logro/:id — what friends see when they open a shared card. */
export function SharePage({ id }: { id: string }) {
  const [share, setShare] = useState<SimShareResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    simApi
      .shared(id)
      .then(setShare)
      .catch((err: unknown) => setError(simErrorMessage(err)));
  }, [id]);
  if (error) {
    return (
      <div className="space-y-4">
        <Notice tone="error">Esta tarjeta ya no existe.</Notice>
        <button type="button" className={primaryButton} onClick={() => navigate('/simulador')}>
          Conocer el juego
        </button>
      </div>
    );
  }
  if (!share) return <Working label="Cargando…" />;
  const c = share.card;
  return (
    <div className="space-y-5">
      <PageTitle kicker={c.kicker}>{c.title}</PageTitle>
      <img
        src={share.image_story}
        alt={`Tarjeta de ${c.nickname}: ${c.phrase}`}
        width={1080}
        height={1920}
        className="mx-auto h-auto max-h-[60dvh] w-auto rounded-2xl border border-gold/60 shadow-lg"
      />
      <p className="text-lg">
        <strong>{c.nickname}</strong> juega a <em>De cirio a hermano mayor</em> con su hermandad,{' '}
        {c.brotherhood}. Funda la tuya y empieza de cirio en el último tramo.
      </p>
      <button
        type="button"
        className={primaryButton}
        onClick={() => navigate('/simulador/registro')}
      >
        Empezar mi carrera
      </button>
      <button type="button" className={secondaryButton} onClick={() => navigate('/simulador')}>
        ¿Qué es esto?
      </button>
      <ReportButton
        what="esta tarjeta"
        onSend={(reason, comment) => simApi.reportShare(id, reason, comment)}
      />
    </div>
  );
}
